import { useEffect, useMemo, useState, useContext } from "react";
import api from "../api";
import { UserContext } from "../context/UserContext";
import ManualBadgeDialog from "../components/admin/ManualBadgeDialog";
import "./home.css";

const badge = (text, color) => (
  <span style={{
    padding: "2px 8px", borderRadius: 999, fontSize: 12,
    background: color, color: "white"
  }}>{text}</span>
);

const StatusBadge = ({ status }) => {
  if (status === "active")   return badge("aktiv",   "#2e7d32");
  if (status === "pending")  return badge("wartend", "#f9a825");
  if (status === "rejected") return badge("abgelehnt","#d32f2f");
  return badge(status, "#9e9e9e");
};

const LockBadge = ({ lockUntil }) => {
  if (!lockUntil) return null;
  const until = new Date(lockUntil);
  if (Date.now() < until.getTime()) {
    const mins = Math.ceil((until.getTime() - Date.now()) / 60000);
    return <span style={{ marginLeft: 8 }}>{badge(`gesperrt ~${mins}m`, "#d32f2f")}</span>;
  }
  return null;
};

export default function AdminUsers() {
  const [badgeDialogUserId, setBadgeDialogUserId] = useState(null);
  const [list, setList] = useState([]);
  const [roles, setRoles] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(""); // '', 'pending', 'active', 'rejected'
  const [role, setRole] = useState("");     // '', 'member', 'admin'
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [orderBy, setOrderBy] = useState("createdAt");
  const [orderDir, setOrderDir] = useState("DESC");
  const [meta, setMeta] = useState({ total:0, totalPages:1 });
  const [refFilter, setRefFilter] = useState("");
  const [hasRefOnly, setHasRefOnly] = useState(false);
  const [editingReferrerId, setEditingReferrerId] = useState(null);
  const [refEditValue, setRefEditValue] = useState('');
  const [refEditError, setRefEditError] = useState('');
  const [refEditLoading, setRefEditLoading] = useState(false);
  const [refSuggestions, setRefSuggestions] = useState([]);
  const [refSuggestLoading, setRefSuggestLoading] = useState(false);
  const [refSuggestError, setRefSuggestError] = useState('');
  const { user } = useContext(UserContext);
  const isAdmin = !!user?.roles?.includes('admin');


  const load = async () => {
    setLoading(true);
    try {
        const params = { page, limit: pageSize, orderBy, orderDir };
        if (q.trim()) params.q = q.trim();
        if (status) params.status = status;
        if (role) params.role = role;
        if (hasRefOnly) params.hasReferrer = 'true';
        if (!hasRefOnly && refFilter.trim()) params.referrer = refFilter.trim();
        const { data } = await api.get("/admin/users", { params });
        setList(data.items);
        setMeta({ total: data.total, totalPages: data.totalPages });
    } catch (e) {
        console.error("ADMIN /users error:", e);
        alert(e?.response?.data?.error || e.message || "Fehler beim Laden der Benutzer");
    } finally {
        setLoading(false);
    }
  };

  const applyFilters = () => { setPage(1); load(); };

  const thSort = (label, key) => (
    <th
        style={{padding:"8px", cursor:"pointer", userSelect:"none"}}
        onClick={() => {
        if (orderBy === key) setOrderDir(orderDir === "ASC" ? "DESC" : "ASC");
        else { setOrderBy(key); setOrderDir("ASC"); }
        setPage(1);
        setTimeout(load, 0);
        }}
        title={`Sortieren nach ${label}`}
    >
        {label} {orderBy === key ? (orderDir === "ASC" ? "▲" : "▼") : ""}
    </th>
  );

  useEffect(() => { load(); }, [page, pageSize, orderBy, orderDir]);
  useEffect(() => {
    api.get("/roles").then(res => {
      const sorted = [...res.data].sort((a,b) => a.order-b.order);
      setRoles(sorted);
    });
  }, []);

  const summary = useMemo(() => {
    const s = { total: list.length, active:0, pending:0, rejected:0, admin:0, member:0, moderator:0, mentor:0, anwärter:0, owner:0 };
    for (const u of list) {
      s[u.status]++;
      if (Array.isArray(u.roles)) {
        u.roles.forEach(r => { if (s[r] !== undefined) s[r]++; });
      }
    }
    return s;
  }, [list]);

  const approve = async (id) => { await api.post(`/admin/${id}/approve`); await load(); };
  const reject  = async (id) => { await api.post(`/admin/${id}/reject`);  await load(); };
  const unlock  = async (id) => { await api.post(`/admin/${id}/unlock`);  await load(); };

  const setUserRoles = async (id, newRoles) => {
    await api.patch(`/admin/${id}/role`, { roles: newRoles });
    await load();
  };
  const setUserStatus = async (id, newStatus) => {
    await api.patch(`/admin/${id}/status`, { status: newStatus });
    await load();
  };

  const startEditReferrer = (u) => {
    setEditingReferrerId(u.id);
    setRefEditValue(u.referrer ? u.referrer.username : '');
    setRefEditError('');
  };
  const cancelEditReferrer = () => {
    setEditingReferrerId(null);
    setRefEditValue('');
    setRefEditError('');
  };
  const saveReferrer = async (id) => {
    setRefEditLoading(true);
    try {
      const val = refEditValue.trim();
      if (!val) {
        setRefEditError('Bitte einen Username eingeben oder Entfernen wählen.');
        setRefEditLoading(false);
        return;
      }
      await api.patch(`/admin/${id}/referrer`, { referrer: val });
      cancelEditReferrer();
      await load();
    } catch (e) {
      setRefEditError(e?.response?.data?.error || e.message || 'Fehler beim Speichern');
    } finally {
      setRefEditLoading(false);
    }
  };
  const removeReferrer = async (id) => {
    setRefEditLoading(true);
    try {
      await api.patch(`/admin/${id}/referrer`, { referrer: null });
      cancelEditReferrer();
      await load();
    } catch (e) {
      setRefEditError(e?.response?.data?.error || e.message || 'Fehler beim Entfernen');
    } finally {
      setRefEditLoading(false);
    }
  };

  // Debounced suggestions while editing referrer
  useEffect(() => {
    if (editingReferrerId === null) { setRefSuggestions([]); return; }
    const term = refEditValue.trim();
    if (!term) { setRefSuggestions([]); return; }
    setRefSuggestError('');
    setRefSuggestLoading(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get('/admin/referrer/search', { params: { q: term } });
        setRefSuggestions(Array.isArray(data?.items) ? data.items : []);
      } catch (e) {
        setRefSuggestError(e?.response?.data?.error || e.message || 'Fehler beim Suchen');
        setRefSuggestions([]);
      } finally {
        setRefSuggestLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [editingReferrerId, refEditValue]);

  return (
    <div style={{ width: '100vw', margin: 0, padding: 0 }}>
      <div className="home-page">
        <section style={{width:'100vw', margin:0, padding:0, position:'relative', left:'-120px'}}>
          <h2 className="hero-title" style={{ textAlign:'center', margin:'0 0 8px' }}>Benutzerverwaltung</h2>
          <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign:'center', maxWidth:760 }}>
            Verwalte Mitglieder, Rollen und Zugänge.<br/>
            Filtere, sortiere und bearbeite Nutzer direkt im System.
          </p>
        </section>

        <section style={{width:'100%',margin:0,padding:0}}>
          <div className="highlight-card" style={{gap:18, padding:32, width:'100%'}}>
            <div style={{display:"grid", gridTemplateColumns:"1fr 160px 160px 160px 160px 120px auto", gap:12, alignItems:"center", marginBottom:16}}>
              <input className="hero-btn-secondary" style={{padding:"12px 18px", fontSize:"1rem"}} placeholder="Suche (Name/E-Mail)" value={q} onChange={e=>setQ(e.target.value)} />
              <select className="hero-btn-secondary" style={{padding:"12px 18px", fontSize:"1rem"}} value={status} onChange={e=>setStatus(e.target.value)}>
                <option value="">Status: alle</option>
                <option value="pending">wartend</option>
                <option value="active">aktiv</option>
                <option value="rejected">abgelehnt</option>
              </select>
              <select className="hero-btn-secondary" style={{padding:"12px 18px", fontSize:"1rem"}} value={role} onChange={e=>setRole(e.target.value)}>
                <option value="">Rolle: alle</option>
                <option value="member">member</option>
                <option value="admin">admin</option>
                <option value="moderator">moderator</option>
                <option value="mentor">mentor</option>
                <option value="anwärter">anwärter</option>
                <option value="owner">owner</option>
              </select>
              <input className="hero-btn-secondary" style={{padding:"12px 18px", fontSize:"1rem"}} placeholder="Referrer (Username)" value={refFilter} onChange={e=>{ setRefFilter(e.target.value); setHasRefOnly(false); }} />
              <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13}}>
                <input type="checkbox" checked={hasRefOnly} onChange={e=>{ setHasRefOnly(e.target.checked); if (e.target.checked) setRefFilter(''); }} /> nur mit Referrer
              </label>
              <div style={{display:"flex", gap:8}}>
                <button className="hero-btn-primary" onClick={load} disabled={loading}>{loading ? "Lade..." : "Filtern"}</button>
                <button className="hero-btn-secondary" onClick={()=>{ setQ(""); setStatus(""); setRole(""); setRefFilter(''); setHasRefOnly(false); setList([]); setPage(1); load(); }}>Zurücksetzen</button>
              </div>
            </div>

            <div style={{marginBottom:12, fontSize:13, opacity:.85}}>
              <b>{summary.total}</b> Nutzer — aktiv: {summary.active}, wartend: {summary.pending}, abgelehnt: {summary.rejected}, admin: {summary.admin}, member: {summary.member}
            </div>

            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%", borderCollapse:"collapse"}}>
                  <thead>
                      <tr style={{textAlign:"left", borderBottom:"1px solid #ddd"}}>
                          {thSort("Name","username")}
                          {thSort("Anzeigename","displayName")}
                          {thSort("E-Mail","email")}
                          {thSort("Rolle","role")}
                          {thSort("Status","status")}
                          {thSort("Geworben durch","referrer")}
                          {thSort("Letzter Login","lastLoginAt")}
                          <th style={{padding:"8px"}}>Aktionen</th>
                      </tr>
                  </thead>
                <tbody>
                  {list.map(u => {
                    const locked = u.lockUntil && new Date(u.lockUntil).getTime() > Date.now();
                    return (
                      <tr key={u.id} style={{borderBottom:"1px solid #eee"}}>
                        <td style={{padding:"8px"}}>
                          <a href={`/u/${u.id}`} style={{color:'#32CD89',textDecoration:'underline',cursor:'pointer'}} title={`Profil von ${u.username} öffnen`}>{u.username}</a>
                        </td>
                        <td style={{padding:"8px"}}>
                          {u.displayName ? (
                            <a href={`/u/${u.id}`} style={{color:'#32CD89',textDecoration:'underline',cursor:'pointer'}} title={`Profil von ${u.displayName} öffnen`}>{u.displayName}</a>
                          ) : <span style={{color:'#aaa'}}>-</span>}
                        </td>
                        <td style={{padding:"8px"}}>{u.email}</td>
                        <td style={{padding:"8px"}}>
                          {/* Rollen-Badges anzeigen */}
                          <div style={{display:'flex', gap:4, flexWrap:'wrap', marginBottom:4}}>
                            {Array.isArray(u.roles) && u.roles.map(r => {
                              const info = roles.find(role => role.name === r);
                              return <span key={r} style={{background:info?.color||'#888', color:'white', borderRadius:8, padding:'2px 8px', fontSize:12}}>{(info?.icon||"") + " " + (info?.label||r)}</span>;
                            })}
                          </div>
                          {/* Multi-Select für Rollenbearbeitung */}
                          <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                            {roles.map(roleOpt => (
                              <label key={roleOpt.name} style={{display:'flex',alignItems:'center',gap:4,fontSize:13}}>
                                <input
                                  type="checkbox"
                                  checked={u.roles?.includes(roleOpt.name)}
                                  onChange={e => {
                                    const newRoles = e.target.checked
                                      ? [...(u.roles||[]), roleOpt.name]
                                      : (u.roles||[]).filter(r => r !== roleOpt.name);
                                    setUserRoles(u.id, newRoles);
                                  }}
                                />
                                {roleOpt.icon && roleOpt.icon}
                                <span>{roleOpt.label}</span>
                              </label>
                            ))}
                          </div>
                        </td>
                        <td style={{padding:"8px"}}>
                          <StatusBadge status={u.status} />
                          <LockBadge lockUntil={u.lockUntil} />
                        </td>
                        <td style={{padding:"8px", fontSize:12, minWidth:210}}>
                          {editingReferrerId === u.id ? (
                            <div style={{display:'flex', flexDirection:'column', gap:6}}>
                              <div style={{display:'flex', gap:6}}>
                                <input
                                  style={{flex:1, padding:'4px 8px', fontSize:12}}
                                  placeholder="Referrer Username oder ID"
                                  value={refEditValue}
                                  onChange={e=>setRefEditValue(e.target.value)}
                                  disabled={refEditLoading}
                                />
                                <button className="hero-btn-primary" style={{padding:'4px 8px'}} disabled={refEditLoading} onClick={()=>saveReferrer(u.id)}>Speichern</button>
                                <button className="hero-btn-secondary" style={{padding:'4px 8px'}} disabled={refEditLoading} onClick={()=>removeReferrer(u.id)}>Entfernen</button>
                                <button className="hero-btn-secondary" style={{padding:'4px 8px'}} disabled={refEditLoading} onClick={cancelEditReferrer}>✕</button>
                              </div>
                              {(refEditError || refSuggestError) && <div style={{color:'#d32f2f', fontSize:11}}>{refEditError || refSuggestError}</div>}
                              {refSuggestions.length > 0 && (
                                <div style={{border:'1px solid rgba(255,255,255,0.2)', borderRadius:6, background:'rgba(0,0,0,0.4)', maxHeight:160, overflowY:'auto'}}>
                                  {refSuggestions.map(s => (
                                    <div
                                      key={s.id}
                                      onClick={() => { setRefEditValue(String(s.username)); setRefSuggestions([]); setRefEditError(''); }}
                                      style={{padding:'6px 8px', cursor:'pointer', fontSize:12, display:'flex', justifyContent:'space-between', alignItems:'center'}}
                                    >
                                      <span>{s.displayName || s.username} <span style={{opacity:.6}}>({s.username})</span></span>
                                      <span style={{opacity:.5}}>ID {s.id}</span>
                                    </div>
                                  ))}
                                  {refSuggestLoading && <div style={{padding:'6px 8px', fontSize:11, opacity:.7}}>Lädt …</div>}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div style={{display:'flex', alignItems:'center', gap:8}}>
                              {u.referrer ? (
                                <span title={u.referrer.displayName && u.referrer.displayName !== u.referrer.username ? u.referrer.displayName : ''}>
                                  {u.referrer.displayName && u.referrer.displayName !== u.referrer.username
                                    ? `${u.referrer.displayName} (@${u.referrer.username})`
                                    : `@${u.referrer.username}`}
                                </span>
                              ) : <span style={{opacity:.4}}>–</span>}
                              {isAdmin && (
                                <button
                                  className="hero-btn-secondary"
                                  style={{padding:'4px 8px', fontSize:11}}
                                  title="Referrer bearbeiten"
                                  onClick={()=>startEditReferrer(u)}
                                >Bearbeiten</button>
                              )}
                            </div>
                          )}
                        </td>
                        <td style={{padding:"8px", fontSize:12}}>
                          {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : <span style={{opacity:.6}}>—</span>}
                          {u.failedLoginCount > 0 && (
                            <div style={{opacity:.7}}>Fehlversuche: {u.failedLoginCount}{u.lastFailedAt ? ` (letzter: ${new Date(u.lastFailedAt).toLocaleString()})`:""}</div>
                          )}
                        </td>
                        <td style={{padding:"8px"}}>
                          <div style={{display:"flex", gap:8, flexWrap:"wrap"}}>
                            {u.status === "pending" && (
                              <>
                                <button className="hero-btn-primary" onClick={()=>approve(u.id)}>Freischalten</button>
                                <button className="hero-btn-secondary" onClick={()=>reject(u.id)}>Ablehnen</button>
                              </>
                            )}
                            {locked && <button className="hero-btn-secondary" onClick={()=>unlock(u.id)}>Entsperren</button>}
                            <button className="hero-btn-secondary" onClick={()=>setBadgeDialogUserId(u.id)}>Abzeichen vergeben</button>
                            <select className="hero-btn-secondary" style={{padding:"8px 12px", fontSize:"1rem"}}
                              value={u.status}
                              onChange={e=>setUserStatus(u.id, e.target.value)}
                              title="Status direkt setzen"
                            >
                              <option value="pending">pending</option>
                              <option value="active">active</option>
                              <option value="rejected">rejected</option>
                            </select>
                          </div>
  {/* Dialog für manuelle Abzeichen-Vergabe */}
  <ManualBadgeDialog open={!!badgeDialogUserId} userId={badgeDialogUserId} onClose={()=>setBadgeDialogUserId(null)} />
                        </td>
                      </tr>
                    );
                  })}
                  {list.length === 0 && (
                    <tr><td colSpan="8" style={{padding:"16px", opacity:.7}}>Keine Nutzer gefunden.</td></tr>
                  )}
                </tbody>
              </table>
              <div style={{display:"flex", alignItems:"center", gap:8, marginTop:12}}>
                  <button className="hero-btn-secondary" disabled={page<=1 || loading} onClick={()=>{ setPage(p=>p-1); setTimeout(load,0); }}>« Zurück</button>
                  <span>Seite {page} / {meta.totalPages} — {meta.total} Nutzer</span>
                  <button className="hero-btn-secondary" disabled={page>=meta.totalPages || loading} onClick={()=>{ setPage(p=>p+1); setTimeout(load,0); }}>Weiter »</button>

                  <span style={{marginLeft:12}}>Pro Seite:</span>
                  <select className="hero-btn-secondary" style={{padding:"8px 12px", fontSize:"1rem"}}
                      value={pageSize}
                      onChange={e=>{ setPageSize(Number(e.target.value)); setPage(1); setTimeout(load,0); }}
                  >
                      {[10,20,50,100].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
              </div>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <div style={{width:'100%',margin:0,padding:0}}>
            <h2 className="cta-title">Mitglieder verwalten</h2>
            <p className="cta-desc">Hier kannst du neue Mitglieder freischalten, Rollen ändern und Zugänge steuern.</p>
          </div>
        </section>
      </div>
    </div>
  );
}
